import { useEffect } from 'react';
import { create } from 'zustand';
import { readStoredString } from './readStoredJson';

/**
 * The shared theme source of truth. `<html class="dark">` (+ `data-theme` +
 * `localStorage('theme')`) is what `mini-apps/_shared/components/ThemeToggle.tsx`
 * and `MiniAppLayout.tsx`'s `useMiniAppDarkMode` already treat as canonical —
 * this store reads/writes the SAME DOM state so both are honored without
 * either needing to change (editing `_shared/` forces the ~250-file mini-app
 * test sweep per CLAUDE.md, which a Preferences-only change shouldn't need).
 *
 * A zustand store (not `useSyncExternalStore`) per CLAUDE.md's "Zustand for
 * Module-Scope Client State" rule — `eslint-rules/no-hand-rolled-external-store.js`
 * flags any `useSyncExternalStore` call in `web/app-home/src`, and the
 * `.oxlintrc.json` allowlist says a new store never gets an entry.
 */
export type Theme = 'light' | 'dark';

interface ThemeState {
  theme: Theme;
}

export const initialState: ThemeState = { theme: 'light' };

const readDomTheme = (): Theme =>
  typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light';

export const useThemeStore = create<ThemeState>(() => ({ ...initialState, theme: readDomTheme() }));

/**
 * Writes the SAME three lines `ThemeToggle.tsx`'s toggle() and
 * `useMiniAppDarkMode` write (class + data-theme + localStorage), then
 * mirrors the change into the store so any component reading `useThemeStore`
 * (or `useThemeSync`) re-renders immediately.
 */
export function setTheme(next: Theme): void {
  document.documentElement.classList.toggle('dark', next === 'dark');
  document.documentElement.dataset.theme = next;
  localStorage.setItem('theme', next);
  useThemeStore.setState({ theme: next });
}

/**
 * Resolves the user's stored theme the same way `useMiniAppDarkMode`
 * (`mini-apps/_shared/components/MiniAppLayout.tsx`) does — `localStorage`'s
 * `theme` key, falling back to the legacy `launcher-theme` key, falling back
 * to the OS `prefers-color-scheme` media query — and applies it to `<html>`
 * and the store, the same two DOM writes `setTheme()` makes above. Called
 * once from `main.tsx`, synchronously, before the first paint: Home has no
 * shell of its own that calls `useMiniAppDarkMode`, so without this, a user
 * who starts on Home sees no theme applied at all until they visit a mini-app
 * or Flow shell. Deliberately does NOT call `localStorage.setItem` — an
 * OS-derived default is not a preference the user has chosen, and persisting
 * it would make them unable to tell the difference later.
 */
export function applyStoredTheme(): void {
  const stored = readStoredString(localStorage, 'theme') || readStoredString(localStorage, 'launcher-theme');
  const theme: Theme =
    stored === 'dark' || (!stored && window.matchMedia('(prefers-color-scheme: dark)').matches)
      ? 'dark'
      : 'light';
  document.documentElement.classList.toggle('dark', theme === 'dark');
  document.documentElement.dataset.theme = theme;
  useThemeStore.setState({ theme });
}

/**
 * Keeps the store honest when someone ELSE flips `<html class>` (ThemeToggle,
 * useMiniAppDarkMode, or any other future writer) — observes the DOM
 * attribute directly rather than trusting only `setTheme` calls made through
 * this module. The effect has a cleanup, so it is StrictMode-safe by
 * construction.
 */
export function useThemeSync(): Theme {
  const theme = useThemeStore((s) => s.theme);

  useEffect(() => {
    const sync = () => useThemeStore.setState({ theme: readDomTheme() });
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return theme;
}
